import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../tools/api_Routes";
import { Link } from "react-router-dom";
import { FaPencilAlt } from "react-icons/fa";

const PanelsByTypeTable = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  // Filter states
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [typeFilter, setTypeFilter] = useState("S");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getData = async () => {
    setIsLoading(true);
    const queryParams = [
      `type=${typeFilter}`,
      `per_page=${rowsPerPage}`,
      `page=${currentPage + 1}`,
      `url=${debouncedKeyword}`,
    ].join("&");
    const { response } = await Helper.Get({
      url: `${api_Routes.Panels.View}?${queryParams}`,
      hasToken: true,
    });

    setData(response || []);
    setIsLoading(false);
  };

  useEffect(() => {
    if (filtersChanged || shouldRefresh) {
      setCurrentPage(0);
      setFiltersChanged(false);
      setShouldRefresh(false);
    }
    getData();
  }, [typeFilter, currentPage, rowsPerPage, shouldRefresh, filtersChanged]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeyword(keyword);
      if (keyword) {
        setFiltersChanged(true);
      }
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [keyword]);
  const getTypeLabel = (type) => {
    switch (type) {
      case "S":
        return "Reseller";
      case "P":
        return "Provider";
      case "SP":
        return "Both";
      default:
        return "";
    }
  };

  return (
    <div className="pt-16">
      {/* Filter Section */}
      <div className="flex justify-between items-center flex-col gap-4 mt-5">
        <label className="font-bold inline-block text-textColor dark:text-darkTextColor text-[15px]  text-start">
          Search by url :
          <input
            placeholder="Write any keyword"
            type="text"
            name="keyword"
            value={keyword}
            onChange={handleKeywordChange}
            autoComplete="off"
            className="duration-200 font-normal bg-secondary dark:bg-darkPrimary text-textColor dark:text-darkTextColor ml-2
                           border-[1px] input-box 
                           rounded-[0.25rem]  py-1 px-3 outline-none"
          />
        </label>
        <div className="w-full">
          <FormControl sx={{ bgcolor: "white" }} className="w-full">
            <InputLabel>Panel Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentPage(0);
              }}
              label="Panel Type"
            >
              <MenuItem value="S">Reseller</MenuItem>
              <MenuItem value="P">Provider</MenuItem>
              <MenuItem value="SP">Both</MenuItem>
              <MenuItem value="">All</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Table and Pagination */}
      {isLoading ? (
        <LinearProgress sx={{ marginTop: 1 }} />
      ) : data?.data?.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Panel Name</TableCell>
                    <TableCell>Panel ID</TableCell>
                    <TableCell>Panel URL</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.data.map((panel) => (
                    <TableRow key={panel.smmpanel_id}>
                      <TableCell>{panel.smmpanel_name}</TableCell>
                      <TableCell>{panel.smmpanel_id}</TableCell>
                      <TableCell>
                        <a
                          href={panel.smmpanel_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          {panel.smmpanel_url}
                        </a>
                      </TableCell>
                      <TableCell>{getTypeLabel(panel.type)}</TableCell>
                      <TableCell>
                        <Link
                          to={`/Panels/Edit/${panel?.smmpanel_id}`}
                          color="inherit"
                          underline="none"
                        >
                          <FaPencilAlt className="text-[#6c757d]" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          <div className="flex gap-4 items-center justify-center">
            <TablePagination
              page={currentPage}
              onPageChange={handlePageChange}
              count={data.pagination.total}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
            <button
              onClick={() =>
                setCurrentPage(
                  currentPage ===
                    Math.ceil(data.pagination.total / rowsPerPage) - 1
                    ? 0
                    : Math.ceil(data.pagination.total / rowsPerPage) - 1
                )
              }
            >
              {currentPage ===
              Math.ceil(data.pagination.total / rowsPerPage) - 1 ? (
                <div className="flex -ml-7">
                  <GoChevronRight />
                  <GoChevronRight className="-ml-3" />
                </div>
              ) : (
                <div className="flex">
                  <GoChevronLeft />
                  <GoChevronLeft className="-ml-3" />
                </div>
              )}
            </button>
          </div>
        </>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">No panels found</TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default PanelsByTypeTable;
